import { createI18n } from 'vue-i18n'
import { messages } from '@/i18n/messages'
import { readSharedLocale, takeLocaleFromAddress, writeSharedLocale } from './shared-locale'

export type SupportedLocale = keyof typeof messages

export const LOCALE_STORAGE_KEY = 'app.locale'
export const supportedLocales: SupportedLocale[] = ['fr', 'en', 'ar', 'ma']
export const rtlLocales: SupportedLocale[] = ['ar', 'ma']
export const localeLabels: Record<SupportedLocale, string> = {
  fr: 'Français',
  en: 'English',
  ar: 'العربية',
  ma: 'الدارجة'
}

export function backendLocale(locale: SupportedLocale): SupportedLocale {
  return locale
}

function normalizeLocale(value?: string | null): SupportedLocale {
  const rawLocale = value?.toLowerCase()
  const normalized = rawLocale?.startsWith('ar-ma') ? 'ma' : rawLocale?.split('-')[0]

  // A phone whose language we do not speak belongs, on a Moroccan pilot, to a Darija speaker:
  // French stays the *key* fallback (fallbackLocale) but it is not the first guess for a person.
  return supportedLocales.includes(normalized as SupportedLocale) ? normalized as SupportedLocale : 'ma'
}

export function getStoredLocale(): SupportedLocale {
  // The address (a link from another app said so) > the cookie shared by all three apps > this
  // app's own storage > the browser. See `shared-locale.ts`.
  const own = (() => {
    try {
      return localStorage.getItem(LOCALE_STORAGE_KEY)
    } catch {
      return null
    }
  })()

  return normalizeLocale(takeLocaleFromAddress(supportedLocales) ?? readSharedLocale() ?? own ?? navigator.language)
}

export const i18n = createI18n({
  legacy: false,
  locale: getStoredLocale(),
  fallbackLocale: 'fr',
  messages
})

export function currentLocale(): SupportedLocale {
  return normalizeLocale(i18n.global.locale.value)
}

export function setLocale(locale: SupportedLocale) {
  i18n.global.locale.value = locale
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Storage blocked: the cookie below still carries the choice.
  }
  writeSharedLocale(locale)
  document.documentElement.lang = locale
  document.documentElement.dir = rtlLocales.includes(locale) ? 'rtl' : 'ltr'
}

setLocale(currentLocale())
